import { useEffect, useRef, useState } from 'react';
import { isMobileDevice } from '../../utils/device/mobileDetection';

const KEYBOARD_DELTA_THRESHOLD_PX = 80;

interface UseVisualViewportBottomOffsetOptions {
    enabled?: boolean;
    mobileBreakpoint?: number;
}

function getLayoutViewportHeight(): number {
    if (typeof window === 'undefined') return 0;

    return Math.max(window.innerHeight || 0, document.documentElement?.clientHeight || 0);
}

/**
 * Returns the number of pixels obscured below the visual viewport.
 *
 * Mobile browsers differ on whether `window.innerHeight` shrinks with the keyboard.
 * Keeping the largest recent layout height gives fixed widgets a stable baseline
 * when the keyboard opens and the browser reports only the shrunken viewport.
 */
export function useVisualViewportBottomOffset({
    enabled = true,
    mobileBreakpoint = 1242,
}: UseVisualViewportBottomOffsetOptions = {}): number {
    const [bottomOffset, setBottomOffset] = useState(0);
    const layoutViewportHeightRef = useRef(0);

    useEffect(() => {
        if (typeof window === 'undefined' || !enabled) {
            setBottomOffset(0);
            return;
        }

        const update = () => {
            const vv = window.visualViewport as VisualViewport | undefined;
            const shouldTrack =
                window.innerWidth <= mobileBreakpoint ||
                (typeof navigator !== 'undefined' && isMobileDevice());

            if (!vv || !shouldTrack) {
                layoutViewportHeightRef.current = getLayoutViewportHeight();
                setBottomOffset(0);
                return;
            }

            const currentLayoutHeight = getLayoutViewportHeight();
            const visualViewportBottom = vv.offsetTop + vv.height;
            const previousLayoutHeight = layoutViewportHeightRef.current || currentLayoutHeight;
            const keyboardLikelyVisible =
                previousLayoutHeight - vv.height > KEYBOARD_DELTA_THRESHOLD_PX ||
                currentLayoutHeight - vv.height > KEYBOARD_DELTA_THRESHOLD_PX;

            if (!keyboardLikelyVisible) {
                layoutViewportHeightRef.current = currentLayoutHeight;
                setBottomOffset(0);
                return;
            }

            const layoutViewportHeight = Math.max(
                previousLayoutHeight,
                currentLayoutHeight,
                visualViewportBottom
            );
            const nextOffset = Math.max(0, Math.round(layoutViewportHeight - visualViewportBottom));
            setBottomOffset(nextOffset);
        };

        const handleOrientationChange = () => {
            layoutViewportHeightRef.current = 0;
            setBottomOffset(0);
        };

        update();
        window.addEventListener('resize', update);
        window.addEventListener('orientationchange', handleOrientationChange);

        const vv = window.visualViewport as VisualViewport | undefined;
        if (vv) {
            vv.addEventListener('resize', update);
            vv.addEventListener('scroll', update);
        }

        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('orientationchange', handleOrientationChange);
            if (vv) {
                vv.removeEventListener('resize', update);
                vv.removeEventListener('scroll', update);
            }
        };
    }, [enabled, mobileBreakpoint]);

    return bottomOffset;
}
